/**
 * Decides which Properties Panel section a selection should open automatically.
 *
 * Selection -> default section:
 *  - Text-only (one or more text frames, nothing else)   -> Type
 *  - Exactly one photo, nothing else selected            -> Photo
 *  - Exactly one ClikArt, nothing else selected          -> Photo (relabelled ClikArt)
 *  - A single group (whatever it contains)               -> Object
 *  - Two or more photos                                  -> Object
 *  - Any plain shape, or a mixed selection                -> Object
 *
 * Also derives `key`, an opaque per-selection identity for auto-open memory (see
 * `usePropertiesPanelAutoOpenSection`): it combines the selected ids with the resolved default
 * section so that mutating a shape's *kind* without changing its id (e.g. clearing a placed
 * photo's fill, which reclassifies the selection from Photo to Object) still counts as a new
 * selection, rather than silently reusing whatever was remembered for the old classification.
 */
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isPhotoSlotCandidate, isTextCandidate } from '@/lib/panels/properties-panel-applicability'
import type { PropertiesPanelDefaultSectionId } from '@/lib/panels/types/properties-panel-default-section-id'
import type { PropertiesPanelSelectionSignature } from './types/properties-panel-selection-signature'
import type { ComputePropertiesPanelSelectionSignatureInput } from './types/compute-properties-panel-selection-signature-input'

function resolveDefaultSectionId(selection: ShapeData[]): PropertiesPanelDefaultSectionId | null {
  if (selection.length === 0) return null

  // A group counts as one object regardless of what it contains — Object always opens, never
  // Type/Photo, even when the group holds text or a photo (see the applicability module's own
  // "group counts as one object" rule).
  if (selection.length === 1 && isGroupShape(selection[0])) return 'object'

  if (selection.every(isTextCandidate)) return 'type'

  if (selection.length === 1 && isPhotoSlotCandidate(selection[0])) return 'photo'

  // Any plain shape, two-or-more photos, or a mixed selection.
  return 'object'
}

export function computePropertiesPanelSelectionSignature({
  shapes,
  textBoxes,
}: ComputePropertiesPanelSelectionSignatureInput): PropertiesPanelSelectionSignature {
  const selection = [...shapes, ...textBoxes]
  const defaultSectionId = resolveDefaultSectionId(selection)

  if (defaultSectionId === null) return { key: null, defaultSectionId: null }

  const ids = selection.map((shape) => shape.id).sort()

  return { key: `${ids.join(',')}::${defaultSectionId}`, defaultSectionId }
}
